const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://docs-waba.hawktrion.com').replace(/\/+$/, '');

function slugify(value) {
  return String(value || '')
    .replace(/\{([^}]+)\}/g, '$1')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'endpoint';
}

function hashString(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function createOperationSlug(operation) {
  const identifier = `${operation.methodLower || operation.method.toLowerCase()}:${operation.path}`;
  return `${operation.method.toLowerCase()}-${slugify(operation.path)}-${hashString(identifier)}`;
}

export function createOperationUrl(operation) {
  return `/api/${createOperationSlug(operation)}`;
}

export function createGroupAnchor(group) {
  return slugify(group);
}

function cleanDescription(description) {
  return String(description || '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function createKeywords(operation) {
  const pathTerms = operation.path
    .split('/')
    .flatMap((segment) => segment.replace(/[{}]/g, '').split(/[-_]/));
  const summaryTerms = operation.summary.split(/\s+/);
  const keywords = [
    'Hawktrion',
    'WhatsApp Business API',
    operation.tags?.[0],
    operation.method,
    ...summaryTerms,
    ...pathTerms
  ]
    .filter(Boolean)
    .map((keyword) => keyword.trim())
    .filter(Boolean);

  return [...new Set(keywords)].join(', ');
}

export function getOperationDescription(operation) {
  const detail = cleanDescription(operation.description);
  const endpoint = `${operation.method} ${operation.path}`;
  const context = detail ? ` ${detail}` : ' Review parameters, authentication, request examples, and responses.';
  const description = `${operation.summary} in the WhatsApp Business API: ${endpoint}.${context}`;

  if (description.length <= 160) {
    return description;
  }

  const availableDetailLength = Math.max(0, 157 - `${operation.summary} in the WhatsApp Business API: ${endpoint}. `.length);
  const prefix = `${operation.summary} in the WhatsApp Business API: ${endpoint}.`;
  return `${prefix} ${detail.slice(0, availableDetailLength).trimEnd()}...`;
}

export function createOperationSeo(operation, siteUrl = SITE_URL) {
  const group = operation.tags?.[0] || 'API Reference';
  const url = `${siteUrl}${createOperationUrl(operation)}`;
  const title = `${operation.summary} (${operation.method} ${operation.path}) | Hawktrion API Docs`;
  const description = getOperationDescription(operation);

  return {
    title,
    description,
    url,
    keywords: createKeywords(operation),
    ogType: 'article',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'TechArticle',
          headline: title,
          description,
          keywords: createKeywords(operation),
          articleSection: group,
          url,
          inLanguage: 'en',
          publisher: {
            '@type': 'Organization',
            name: 'Hawktrion',
            url: 'https://hawktrion.com/'
          }
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'API documentation', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: group, item: `${siteUrl}/#${createGroupAnchor(group)}` },
            { '@type': 'ListItem', position: 3, name: operation.summary, item: url }
          ]
        }
      ]
    }
  };
}

const OVERVIEW_PAGES = {
  authentication: {
    title: 'WhatsApp Business API Authentication | Hawktrion Docs',
    description: 'Learn how to create and use a Hawktrion API access token to authenticate WhatsApp Business API requests.',
    heading: 'Authentication'
  },
  servers: {
    title: 'WhatsApp Business API Servers | Hawktrion Docs',
    description: 'Review the available Hawktrion API environments and choose the correct server for WhatsApp Business API requests.',
    heading: 'Servers'
  },
  terms: {
    title: 'WhatsApp Business API Terms | Hawktrion Docs',
    description: 'Review the applicable Hawktrion and WhatsApp Business Platform terms before using the API.',
    heading: 'Terms'
  }
};

export function createOverviewSeo(overviewId = 'authentication', siteUrl = SITE_URL) {
  const page = OVERVIEW_PAGES[overviewId] || OVERVIEW_PAGES.authentication;
  const path = overviewId === 'authentication' ? '/' : `/overview/${overviewId}`;
  const url = `${siteUrl}${path}`;

  return {
    title: page.title,
    description: page.description,
    url,
    keywords: `Hawktrion, WhatsApp Business API, ${page.heading.toLowerCase()}, API documentation`,
    ogType: overviewId === 'authentication' ? 'website' : 'article',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: page.heading,
      description: page.description,
      url
    }
  };
}
